/**
 * ToolGrid.jsx
 * ============
 * Renders manifest tools grouped by category.
 *
 * Desktop (md+): full sidebar — each category is a labeled section with
 *   stacked tool buttons showing a Lucide icon + display_name.
 *
 * Mobile: a compact two-row strip pinned above ChatPanel:
 *   Row 1 — horizontally scrollable category pills (tabs)
 *   Row 2 — horizontally scrollable tool buttons for the active category
 *
 * Props
 * -----
 *   groups      – [{ category: string, tools: ToolManifest[] }] from useManifestGroups
 *   onToolClick – (tool: ToolManifest) => void
 */

import { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';

/** Convert kebab-case icon name → PascalCase → Lucide component, fallback Zap. */
function getIcon(iconName, size = 16) {
  if (!iconName) return <LucideIcons.Zap size={size} />;
  const pascal = iconName
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join('');
  const Icon = LucideIcons[pascal];
  return Icon ? <Icon size={size} /> : <LucideIcons.Zap size={size} />;
}

/** Use display_name if present, otherwise humanise the raw name. */
function displayName(tool) {
  if (tool.display_name) return tool.display_name;
  return (tool.name || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

/** Loading placeholder — adapts to desktop / mobile layout slot. */
function LoadingPlaceholder() {
  return (
    <aside className="flex flex-col h-full w-64 flex-shrink-0 border-r border-[#2a2a2a] bg-[#161616] p-4">
      <p className="text-[#555555] text-sm italic">Loading tools…</p>
    </aside>
  );
}

export default function ToolGrid({ groups, onToolClick }) {
  const [activeCategory, setActiveCategory] = useState(null);

  // Auto-select first category when groups first load.
  useEffect(() => {
    if (groups.length > 0 && activeCategory === null) {
      setActiveCategory(groups[0].category);
    }
  }, [groups, activeCategory]);

  if (!groups || groups.length === 0) {
    return <LoadingPlaceholder />;
  }

  const activeGroup =
    groups.find(g => g.category === activeCategory) ?? groups[0];

  return (
    <>
      {/* ── Sidebar (desktop full-width; mobile inside overlay) ────────── */}
      <aside className="flex flex-col h-full w-64 shrink-0 border-r border-[#2a2a2a] bg-[#161616]">
        <div className="flex-1 overflow-y-auto px-3 py-3 pb-6 bg-[#161616] min-h-full">
          {groups.map(({ category, tools }, groupIdx) => (
            <div key={category}>
              {/* Category label */}
              <h3 className={`text-[#CC5500] text-xs font-semibold uppercase tracking-widest mb-2 px-1 ${groupIdx === 0 ? 'mt-0' : 'mt-4'}`}>
                {category}
              </h3>

              {/* Tool buttons */}
              <div className="flex flex-col gap-1">
                {tools.map(tool => (
                  <button
                    key={tool.name}
                    onClick={() => onToolClick(tool)}
                    title={tool.description || displayName(tool)}
                    className="
                      text-left w-full px-3 py-2 rounded text-sm text-white
                      bg-[#1e1e1e] border border-[#2a2a2a]
                      hover:bg-[#2a2a2a] hover:border-[#CC5500]
                      flex items-center gap-2
                      transition-colors duration-150 cursor-pointer
                      focus:outline-none focus:border-[#CC5500]
                    "
                  >
                    <span className="text-[#CC5500] flex-shrink-0" aria-hidden="true">
                      {getIcon(tool.icon, 16)}
                    </span>
                    <span className="truncate">{displayName(tool)}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </aside>
    </>
  );
}
